import fp from 'lodash/fp';
import { put, select, all, fork, take, cancel, takeEvery, throttle } from 'redux-saga/effects';
import { mapFiltersToApi, mapSortingToApi, applyFiltersLocal } from '@factorin/domain';
import { API, data } from '@factorin/container.macro';

import { apiCall } from 'Modules/core/api';
import { applyPath } from 'Modules/core/redux';
import { messagingActions } from 'Modules/core/messaging';

import { paginationActions } from './actions';
import { storageActions } from '../storage/actions';
import { loadStorageItem } from '../storage/sagas';
import { paginationStatuses } from './statuses';
import { getDomainTypeFromPath } from './utils';


const getPagedParameters = (start, stop) => {
    if (start === stop) {
        return {
            pageSize: 0,
            pageNumber: 0,
            overflowLeft: 0,
            overflowRight: 0,
        };
    }

    let pageSize = stop - start;
    let pageNumber = Math.floor(stop / pageSize);
    while ((pageNumber - 1) * pageSize > start || pageNumber * pageSize < stop) {
        if (pageNumber * pageSize < stop)
            pageSize++;
        else
            pageNumber--;
    }
    return {
        pageSize,
        pageNumber,
        overflowLeft: start - (pageNumber - 1) * pageSize,
        overflowRight: pageNumber * pageSize - stop,
    };
};

function* fetchData(path, type, start, stop) {
    const parameters = yield select(({ data }) => data.pagination[path].parameters);

    // TODO: make backend more index-based pagination-friendly
    const { pageSize, pageNumber, overflowLeft, overflowRight } = getPagedParameters(start, stop);

    const sorting = mapSortingToApi(parameters);
    const filters = yield select(state =>
        mapFiltersToApi(parameters.filters, data.domain[type].filters, state.environment));

    let response;
    const isWampRequest = !!API.domain[type].wampList;
    if (isWampRequest) {
        response = yield API.domain[type].wampList({
            query: {
                pageSize,
                pageNumber,
                ...sorting,
                ...filters,
            },
        });
    } else {
        response = yield apiCall(API.domain[type].list, {
            query: {
                pageSize,
                pageNumber,
                ...sorting,
                ...filters,
            },
        });
    }

    const { Page, ItemsCount: total } = response;
    return {
        elements: (Page || []).slice(overflowLeft, pageSize - overflowRight),
        total,
    };
}

const mapToStorage = fp.keyBy(({ Id, Uid, id }) => Uid || Id || id);

function* request(path, start, stop) {
    const { setQueue, linkQueue, setTotal, markDirty, receive, setError } = applyPath(
        path,
        paginationActions,
    );

    const setQueueStatus = status => put(setQueue({ start, stop, status }));

    yield setQueueStatus(paginationStatuses.PENDING);
    yield put(linkQueue({ start, stop }));

    const type = getDomainTypeFromPath(path);

    try {
        const { elements, total } = yield fetchData(path, type, start, stop);
        const oldTotal = yield select(({ data }) => data.pagination[path].total);

        if (fp.isUndefined(oldTotal))
            yield put(setTotal(total));
        else if (oldTotal !== total)
            yield put(markDirty({}));

        yield put(storageActions.receive(type, mapToStorage(elements)));
        yield put(receive({ elements, start, stop }));

        yield setQueueStatus(paginationStatuses.COMPLETE);
    } catch (err) {
        if (process.env.NODE_ENV === 'development') {
            console.error('pagination error');
            console.error(err);
        }
        yield setQueueStatus(paginationStatuses.ERROR);
        yield put(setError(err));
    }
}

function* requestWithRefresh(path, start, stop) {
    const { setQueue, receiveRefresh, setError } = applyPath(path, paginationActions);

    const setQueueStatus = status => put(setQueue({ start, stop, status }));

    yield setQueueStatus(paginationStatuses.PENDING);

    const type = getDomainTypeFromPath(path);

    try {
        const { elements, total } = yield fetchData(path, type, start, stop);

        yield put(storageActions.receive(type, mapToStorage(elements)));
        yield put(receiveRefresh({ elements, total, start, stop }));

        yield setQueueStatus(paginationStatuses.COMPLETE);
    } catch (err) {
        if (process.env.NODE_ENV === 'development') {
            console.error('pagination error');
            console.error(err);
        }
        yield setQueueStatus(paginationStatuses.ERROR);
        yield put(setError(err));
    }
}

function* performGenericRequest(genericRequest, path, start, stop) {
    const status = yield select(({ data }) => data.pagination[path].queue[`${start}:${stop}`]);

    if (status && status === paginationStatuses.PENDING)
        return;

    const requestTask = yield fork(genericRequest, path, start, stop);

    if (status && status === paginationStatuses.ERROR) {
        yield cancel(requestTask);
        return;
    }

    const isCancelled = fp.isEqual({
        start,
        stop,
        status: paginationStatuses.CANCELLED,
    });

    yield take(
        ({ type, path: pathToCancel, $payload }) =>
            pathToCancel === path &&
            (type === `${paginationActions.setParameters}` ||
                type === `${paginationActions.setQueue}` && isCancelled($payload)),
    );

    yield cancel(requestTask);
}

function* performRequest({ path, $payload: { start, stop } }) {
    yield performGenericRequest(request, path, start, stop);
}

function* performRequestWithRefresh({ path, $payload: { start, stop } }) {
    yield performGenericRequest(requestWithRefresh, path, start, stop);
}

function* setParametersThrottled({ path, $payload }) {
    yield put(paginationActions.setParameters(path, $payload));
}

const getUpdatedPaginationsPaths = (domain, entity, paginations) =>
    fp.pipe(
        fp.toPairs,
        fp.filter(
            ([
                path,
                {
                    elements,
                    parameters: { filters },
                },
            ]) =>
                getDomainTypeFromPath(path) === domain &&
                (Object.values(elements).includes(entity.Uid) ||
                    applyFiltersLocal(entity, data.domain[domain].filters, filters)),
        ),
        fp.map(0),
    )(paginations);

function* markDirty(path) {
    const oldDirty = yield select(fp.get(`data.pagination[${path}].dirty`));
    if (oldDirty)
        yield put(paginationActions.markDirty(path, { dirty: false }));

    yield put(paginationActions.markDirty(path, {}));
}

function* markDirtyByType({ $payload: { EntityType, Uid } }) {
    if (!Uid || Uid === 0)
        return;

    try {
        const domain = `${EntityType}s`;
        const oldEntity = yield select(fp.get(`data.storage[${domain}][${Uid}]`));

        const pagination = yield select(fp.get('data.pagination'));

        const oldUpdatedPaginationsPaths = oldEntity
            ? getUpdatedPaginationsPaths(domain, oldEntity, pagination)
            : [];

        const newEntity = yield loadStorageItem({ path: domain, $payload: Uid });
        const newUpdatedPaginationsPaths = getUpdatedPaginationsPaths(
            domain,
            newEntity,
            pagination,
        );
        yield all(
            fp
                .union(newUpdatedPaginationsPaths, oldUpdatedPaginationsPaths)
                .map(path => markDirty(path)),
        );
    } catch (error) {
        console.log(error);
        yield put(
            messagingActions.showNotification({
                content: 'pagination.markDirtyError',
            }),
        );
    }
}

const domainsExtendProps = {
    documents: ['$errorDocument'],
    registers: ['Documents'],
    transportRegisters: ['Documents'],
};

function* refreshPagination({ path, ...rest }) {
    const domain = getDomainTypeFromPath(path);

    const omitFields = fp.getOr([], domain, domainsExtendProps);
    if (omitFields.length !== 0) {
        const storage = yield select(fp.get(`data.storage[${domain}]`));
        const pagination = yield select(fp.get(`data.pagination[${path}]`));
        if (!pagination)
            return;
        yield put(
            storageActions.receiveWithUpdate(
                domain,
                fp.pipe(
                    fp.get('elements'),
                    fp.map(elem => fp.omit(omitFields, storage[elem])),
                    mapToStorage,
                )(pagination),
            ),
        );
    }

    yield put(paginationActions.reducerRefresh(path, rest?.$payload));
}

function* massRefreshPaginations({ $payload: basePath = '' }) {
    const paginations = yield select(fp.get('data.pagination'));
    const updatedPaginations = fp.pipe(fp.keys, fp.filter(fp.startsWith(basePath)))(paginations);

    yield all(fp.map(path => refreshPagination({ path }), updatedPaginations));
}

export function* refreshAllPaginations() {
    yield massRefreshPaginations({});
}

export const sagas = [
    takeEvery(`${paginationActions.request}`, performRequest),
    takeEvery(`${paginationActions.requestRefresh}`, performRequestWithRefresh),
    // TODO: make custom throttle based on path with variable delay
    throttle(1000, `${paginationActions.setParametersThrottled}`, setParametersThrottled),
    takeEvery(`${paginationActions.markDirtyByType}`, markDirtyByType),
    takeEvery(`${paginationActions.refresh}`, refreshPagination),
    takeEvery(`${paginationActions.massRefresh}`, massRefreshPaginations),
];
